function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@ui/primitives/utils';
import { Search } from 'lucide-react';
import { useMemo, useRef } from 'react';
const DEFAULT_TABLE_WIDTH = 1035;
const DEFAULT_COLUMN_WIDTH = 150;
const DEFAULT_ROW_HEIGHT = 85;
function getCommonPinningStyles(column) {
    const isPinned = column.getIsPinned();
    return {
        left: isPinned === 'left' ? "".concat(column.getStart('left').toString(), "px") : undefined,
        right: isPinned === 'right' ? "".concat(column.getAfter('right').toString(), "px") : undefined,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0
    };
}
function renderHeader(header) {
    const context = header.getContext();
    if (typeof header.column.columnDef.header !== 'string') {
        var _header_column_columnDef_header, _header_column_columnDef;
        return flexRender((_header_column_columnDef_header = (_header_column_columnDef = header.column.columnDef).header) === null || _header_column_columnDef_header === void 0 ? void 0 : _header_column_columnDef_header.call(_header_column_columnDef, context), context);
    }
    return flexRender(header.column.columnDef.header, context);
}
function getClassName(column) {
    let result = 'w-full text-center content-center ';
    result = column.getCanSort() ? result + 'cursor-pointer select-none' : result;
    result = !column.getIsPinned() ? result + ' secondary-header-link' : result;
    return result;
}
export const TableVirtualizer = (param)=>{
    let { data, columns, tableConfig, pinnedColumns, emptyStateText } = param;
    const table = useReactTable({
        data,
        columns,
        state: {
            columnVisibility: {},
            columnOrder: []
        },
        initialState: {
            columnPinning: {
                left: pinnedColumns === null || pinnedColumns === void 0 ? void 0 : pinnedColumns.left,
                right: pinnedColumns === null || pinnedColumns === void 0 ? void 0 : pinnedColumns.right
            }
        },
        getCoreRowModel: getCoreRowModel()
    });
    const tableContainerRef = useRef(null);
    const visibleColumns = table.getVisibleLeafColumns();
    const { rows } = table.getRowModel();
    const columnVirtualizer = useVirtualizer({
        count: visibleColumns.length,
        estimateSize: (index)=>{
            var _visibleColumns_index;
            return ((_visibleColumns_index = visibleColumns[index]) === null || _visibleColumns_index === void 0 ? void 0 : _visibleColumns_index.getSize()) || DEFAULT_COLUMN_WIDTH;
        },
        getScrollElement: ()=>tableContainerRef.current,
        horizontal: true,
        overscan: 5
    });
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: ()=>DEFAULT_ROW_HEIGHT,
        getScrollElement: ()=>tableContainerRef.current,
        overscan: 5
    });
    const virtualColumns = useMemo(()=>{
        const items = columnVirtualizer.getVirtualItems();
        if (items.length === 0 && visibleColumns.length > 0) {
            return visibleColumns.map((_, index)=>({
                    index,
                    start: index * DEFAULT_COLUMN_WIDTH,
                    end: (index + 1) * DEFAULT_COLUMN_WIDTH,
                    size: DEFAULT_COLUMN_WIDTH,
                    key: "col-".concat(index)
                }));
        }
        return items;
    }, [
        columnVirtualizer,
        visibleColumns.length
    ]);
    const virtualRows = useMemo(()=>{
        const items = rowVirtualizer.getVirtualItems();
        if (items.length === 0 && rows.length > 0) {
            return rows.map((_, index)=>({
                    index,
                    start: index * DEFAULT_ROW_HEIGHT,
                    end: (index + 1) * DEFAULT_ROW_HEIGHT,
                    size: DEFAULT_ROW_HEIGHT,
                    key: "row-".concat(index)
                }));
        }
        return items;
    }, [
        rowVirtualizer,
        rows.length
    ]);
    const getTableWidth = ()=>{
        if (tableConfig === null || tableConfig === void 0 ? void 0 : tableConfig.width) {
            return tableConfig.width;
        }
        const width = visibleColumns.reduce((sum, col)=>{
            return sum + (col.getSize() || DEFAULT_COLUMN_WIDTH);
        }, 0);
        return width < DEFAULT_TABLE_WIDTH ? DEFAULT_TABLE_WIDTH : width;
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            !!data.length && /*#__PURE__*/ _jsx("div", {
                ref: tableContainerRef,
                className: "overflow-auto relative",
                style: {
                    height: (tableConfig === null || tableConfig === void 0 ? void 0 : tableConfig.height) ? "".concat(tableConfig.height.toString(), "px") : '100%',
                    width: (tableConfig === null || tableConfig === void 0 ? void 0 : tableConfig.width) ? "".concat(tableConfig.width.toString(), "px") : "".concat(DEFAULT_TABLE_WIDTH.toString(), "px")
                },
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "border-separate border-spacing-0",
                    style: {
                        width: "".concat(getTableWidth().toString(), "px")
                    },
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-background sticky top-0 z-10",
                            style: {
                                display: 'grid',
                                width: "".concat(getTableWidth().toString(), "px"),
                                maxHeight: '100px'
                            },
                            children: table.getHeaderGroups().map((headerGroup)=>/*#__PURE__*/ _jsx("tr", {
                                    style: {
                                        display: 'flex',
                                        width: "".concat(getTableWidth().toString(), "px")
                                    },
                                    children: virtualColumns.map((vc)=>{
                                        const header = headerGroup.headers[vc.index];
                                        if (!header) return null;
                                        return /*#__PURE__*/ _jsx("th", {
                                            className: cn('border-b border-border px-4 py-2 text-center text-sm font-medium text-muted-foreground', header.column.getIsPinned() && 'bg-card'),
                                            style: _object_spread({
                                                display: 'flex'
                                            }, getCommonPinningStyles(header.column)),
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: getClassName(header.column),
                                                onClick: header.column.getToggleSortingHandler(),
                                                children: renderHeader(header)
                                            })
                                        }, header.id);
                                    })
                                }, headerGroup.id))
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            style: {
                                display: 'grid',
                                height: "".concat(rowVirtualizer.getTotalSize().toString(), "px"),
                                position: 'relative',
                                width: "".concat(getTableWidth().toString(), "px")
                            },
                            children: virtualRows.map((virtualRow)=>{
                                const row = rows[virtualRow.index];
                                if (!row) return null;
                                const visibleCells = row.getVisibleCells();
                                return /*#__PURE__*/ _jsx("tr", {
                                    "data-index": virtualRow.index,
                                    style: {
                                        display: 'flex',
                                        position: 'absolute',
                                        transform: "translateY(".concat(virtualRow.start.toString(), "px)"),
                                        width: "".concat(getTableWidth().toString(), "px"),
                                        left: 0
                                    },
                                    children: virtualColumns.map((vc)=>{
                                        const cell = visibleCells[vc.index];
                                        if (!cell) return null;
                                        return /*#__PURE__*/ _jsx("td", {
                                            className: cn('flex items-center justify-center border-b border-border px-4 py-2 text-sm', cell.column.getIsPinned() ? 'bg-card' : 'bg-background'),
                                            style: _object_spread({
                                                height: DEFAULT_ROW_HEIGHT
                                            }, getCommonPinningStyles(cell.column)),
                                            children: flexRender(cell.column.columnDef.cell, cell.getContext())
                                        }, cell.id);
                                    })
                                }, row.id);
                            })
                        })
                    ]
                })
            }),
            !data.length && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "h-12 w-12"
                    }),
                    emptyStateText || /*#__PURE__*/ _jsx("p", {
                        children: "No data found"
                    })
                ]
            })
        ]
    });
};
